import { ThemeSchemaResponse } from '@api';
import { TabLines } from '@design/components/tab-lines/TabLines';
import { useCondensedHeader } from '@terminal/hooks/use-condensed-header/useCondensedHeader';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { LegacyThemeHeader } from '../components/theme-header/LegacyThemeHeader';
import { ThemeHeader } from '../components/theme-header/ThemeHeader';
import styles from './ThemeOverviewHeader.module.scss';

export type ThemeOverviewHeaderProps = {
  theme: ThemeSchemaResponse;
};

export const THEME_OVERVIEW_CONTENT_ID = 'theme-overview-content';
export const THEME_TAB_COMPANIES = 'companies';

export const ThemeOverviewHeader = ({ theme }: ThemeOverviewHeaderProps) => {
  const { t } = useTranslation('widget');
  const { params, setQueryParams } = useQueryParams();
  const activeTab = params[queryKeys.activeSubTab] || 'overview';
  const isScrolledEnough = useCondensedHeader({
    getRootElement: () => document.getElementById(THEME_OVERVIEW_CONTENT_ID),
    deps: [activeTab],
  });
  const isRevamp = useFlags(state =>
    state.getFeatureFlagState('revamp_theme_overview')
  );
  const isCondensed = activeTab !== 'overview' || isScrolledEnough;

  if (!theme) {
    throw new Error('ThemeOverviewHeader: No theme found');
  }

  const tabs = [
    {
      tabKey: 'overview',
      tabLabel: t('widget:themeOverview.overview'),
    },
    {
      tabKey: THEME_TAB_COMPANIES,
      tabLabel: t('widget:themeOverview.companies'),
    },
    {
      tabKey: 'performance',
      tabLabel: t('widget:themeOverview.performance'),
    },
    {
      tabKey: 'earnings',
      tabLabel: t('widget:themeOverview.earnings'),
    },
  ];

  return (
    <div className={styles.themeHeaderRoot} data-testid="theme-overview-header">
      <div
        className={clsx(styles.innerContainer, {
          [styles.condensed]: isCondensed,
        })}
      >
        {isRevamp ? (
          <ThemeHeader
            themeId={theme.id}
            themeName={theme.name}
            logo_url={theme.logo_url}
            isCondensed={isCondensed}
          />
        ) : (
          <LegacyThemeHeader
            themeId={theme.id}
            themeName={theme.name}
            logo_url={theme.logo_url}
            isCondensed={isCondensed}
          />
        )}
        <TabLines
          tabs={tabs}
          activeTab={activeTab}
          onChange={idx =>
            setQueryParams({ [queryKeys.activeSubTab]: tabs[idx].tabKey })
          }
        />
      </div>
    </div>
  );
};
